import React, { useState, useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import httpService from '../../services/httpService';
import { ApiEndpoints, Constants } from '../../constants/appConstants';
import { useExcelImportPopup } from '../Common/useExcelImportPopup';
import { useDataManagementFileUpload } from '../Common/useDataManagementFileUpload';
import { ImportTemplates } from '../../constants/appConstants';
import { createDialogOverlayStyles } from '../../utils/safeCssInjection';
import './StandardsComponent.scss';
import DataManagementForm from '../DataManagement/DataManagementForm';
import { dataManagementFormConfig, getFormValidationErrors, normalizeFieldValues } from '../DataManagement/dataManagementFormConfig';
import DataManagementLoader from '../DataManagement/DataManagementLoader';

const fetchStandards = () => {
  return httpService.get(ApiEndpoints.GetAllStandards);
};


interface StandardsComponentProps {
  providerTypes: { label: string; value: string }[];
}

const StandardsComponent: React.FC<StandardsComponentProps> = ({providerTypes}) => {
  // Fetch performance areas for dropdown
  const [performanceAreas, setPerformanceAreas] = useState<any[]>([]);
  const [lookupsLoading, setLookupsLoading] = useState(true);
  useEffect(() => {
    httpService.get(ApiEndpoints.GetAllPerformanceAreasWithInactive)
      .then((response: any) => {
        let data = response.data;
        if (!Array.isArray(data)) data = [];
        setPerformanceAreas(data);
      })
      .catch(() => setPerformanceAreas([]))
      .finally(() => setLookupsLoading(false));
  }, []);

  React.useEffect(() => {
    const cleanup = createDialogOverlayStyles('standards-component-styles');
    return cleanup;
  }, []);

  const [standards, setStandards] = useState<any[]>([]);
  const toast = useRef<Toast>(null);
  const { uploadFile } = useDataManagementFileUpload();

  const handleExcelUpload = async (file: File) => {
    setLoading(true);
    try {
      const fileKey = await uploadFile(file, Constants.DATA_MANAGEMENT_MODULES.STANDARDS);

      await httpService.post(ApiEndpoints.BulkUploadDataManagement, {
        fileKey,
        fileName: file.name,
        module: Constants.DATA_MANAGEMENT_MODULES.STANDARDS,
      });

      const response = await fetchStandards();
      setStandards(response.data as any[]);
      toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Standards uploaded successfully', life: 3000 });
    } catch (err: any) {
      let msg = err?.response?.data?.message || err?.message || 'Bulk upload failed';
      toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const { showImport, ImportPopup } = useExcelImportPopup({
    templateUrl: ImportTemplates.standards,
    onUpload: handleExcelUpload,
    title: 'Import Standards from Excel',
  });

  const [providerTypeFilter, setProviderTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [selectedStandard, setSelectedStandard] = useState<any>(null);
  const [isAddMode, setIsAddMode] = useState(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [standardToDelete, setStandardToDelete] = useState<any>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 7;

  useEffect(() => {
    setLoading(true);
    fetchStandards()
      .then((response: any) => {
        let data = response.data;
        if (!Array.isArray(data)) data = [];
        setStandards(data);
        setLoading(false);
      })
      .catch((err: any) => {
        console.error(`Failed to fetch standards: ${err.message}`);
        setLoading(false);
      });
  }, []);

  const providerTypeOptions = providerTypes && providerTypes.length > 0
    ? providerTypes: Array.from(new Set(standards.map(s => s.providerType).filter(Boolean))).map(pt => ({ label: pt, value: pt }));

  let filteredStandards = providerTypeFilter ? standards.filter(s => s.providerType === providerTypeFilter) : standards;
  if (search.trim()) {
    filteredStandards = filteredStandards.filter(s =>
      s.title && s.title.toLowerCase().includes(search.trim().toLowerCase())
    );
  }

  const totalPages = Math.ceil(filteredStandards.length / rowsPerPage);
  const paginatedStandards = filteredStandards.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  if (currentPage > totalPages && totalPages > 0) {
    setCurrentPage(1);
  }

  return (
    <div style={{ marginTop: 24, position: 'relative', minHeight: 220 }} aria-busy={loading || lookupsLoading}>
      <DataManagementLoader visible={loading || lookupsLoading} label="Loading standards" />
      <Toast ref={toast} position="top-right" style={{ zIndex: 9999 }} />
      {ImportPopup}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, gap: 16 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <Button label="Add New Standard" icon="pi pi-plus" onClick={() => {
            setSelectedStandard({ standardId: 0, title: '', description: '', isLocationSpecific: false, performanceAreaTitle: '', performanceAreaId: '', isActive: true, providerType: '' });
            setIsAddMode(true);
            setEditDialogVisible(true);
          }} />
          <Button label="Import" icon="pi pi-upload" className="p-button-secondary" onClick={showImport} style={{ marginLeft: 8 }} />
        </div>
        <div className="standard-search-anim">
          <input
            type="text"
            placeholder="Search by Title..."
            value={search}
            onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
          />
          <span className="pi pi-search" />
        </div>
        <div className="standard-provider-type-dropdown">
          <select
            value={providerTypeFilter}
            onChange={e => { setProviderTypeFilter(e.target.value); setCurrentPage(1); }}
          >
            <option value="">All Provider Types</option>
            {providerTypeOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <span className="pi pi-angle-down" />
        </div>
      </div>
      <div style={{ width: '100%', overflowX: 'auto', position: 'relative' }}>
        <table style={{ minWidth: 900, width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
          <thead>
            <tr style={{ background: '#f0f4f8' }}>
              <th style={{ padding: 8, border: '1px solid #b0b6be', width: 60 }}>#</th>
                {/* <th style={{ padding: 8, border: '1px solid #b0b6be', width: 100 }}>Standard ID</th> */}
              <th style={{ padding: 8, border: '1px solid #b0b6be', width: 160 }}>Title</th>
              <th style={{ padding: 8, border: '1px solid #b0b6be', width: 200 }}>Description</th>
              <th style={{ padding: 8, border: '1px solid #b0b6be', width: 130 }}>Is Location Specific</th>
              <th style={{ padding: 8, border: '1px solid #b0b6be', width: 140 }}>Performance Area Title</th>
              <th style={{ padding: 8, border: '1px solid #b0b6be', width: 100 }}>Is Active</th>
              <th style={{ padding: 8, border: '1px solid #b0b6be', width: 120 }}>Provider Type</th>
              <th style={{ padding: 8, border: '1px solid #b0b6be', width: 140 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredStandards.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ textAlign: 'center', padding: 16 }}>No standards found.</td>
              </tr>
            ) : (
              paginatedStandards.map((standard: any, idx: number) => (
                <tr key={standard.standardId || idx} style={{ background: idx % 2 === 0 ? '#fff' : '#f6f8fa' }}>
                  <td style={{ padding: 8, border: '1px solid #eee' }}>{(currentPage - 1) * rowsPerPage + idx + 1}</td>
                  {/* <td style={{ padding: 8, border: '1px solid #eee' }}>{standard.standardId}</td> */}
                  <td style={{ padding: 8, border: '1px solid #eee' }}>{standard.title || '-'}</td>
                  <td style={{ padding: 8, border: '1px solid #eee' }}>{standard.description || '-'}</td>
                  <td style={{ padding: 8, border: '1px solid #eee' }}>{standard.isLocationSpecific ? 'Yes' : 'No'}</td>
                  <td style={{ padding: 8, border: '1px solid #eee' }}>{standard.performanceAreaTitle || '-'}</td>
                  <td style={{ padding: 8, border: '1px solid #eee' }}>{standard.isActive ? 'Yes' : 'No'}</td>
                  <td style={{ padding: 8, border: '1px solid #eee' }}>{standard.providerType || '-'}</td>
                  <td style={{ padding: 8, border: '1px solid #eee' }}>
                    <Button icon="pi pi-pencil" className="p-button-text" style={{ marginRight: 8 }} onClick={() => { setSelectedStandard({ ...standard }); setIsAddMode(false); setEditDialogVisible(true); }} />
                    <Button icon="pi pi-trash" className="p-button-text p-button-danger" onClick={() => { setStandardToDelete(standard); setDeleteDialogVisible(true); }} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div style={{ marginTop: 16, textAlign: 'right', color: '#888', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
        <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid #ccc', background: currentPage === 1 ? '#eee' : '#fff', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}>&lt;</button>
        <span>Page {currentPage} of {totalPages || 1}</span>
        <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages || totalPages === 0} style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid #ccc', background: (currentPage === totalPages || totalPages === 0) ? '#eee' : '#fff', cursor: (currentPage === totalPages || totalPages === 0) ? 'not-allowed' : 'pointer' }}>&gt;</button>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog
        header={isAddMode ? "Add Standard" : `Edit Standard${selectedStandard && selectedStandard.title ? ` - ${selectedStandard.title}` : ''}`}
        visible={editDialogVisible}
        style={{ width: '550px', maxWidth: '90vw' }}
        onHide={() => { setEditDialogVisible(false); setIsAddMode(false); }}
        footer={
          <div>
            <Button label="Cancel" onClick={() => { setEditDialogVisible(false); setIsAddMode(false); }} className="p-button-text" />
            <Button label="Save" onClick={async () => {
              const validationErrors = getFormValidationErrors(dataManagementFormConfig.standards, selectedStandard);
              if (validationErrors.length > 0) {
                toast.current?.show({ severity: 'warn', summary: 'Fix the following', detail: validationErrors.join(', '), life: 4000 });
                return;
              }
              const payload = normalizeFieldValues(dataManagementFormConfig.standards, selectedStandard);
              if (isAddMode) {
                try {
                  setLoading(true);
                  await httpService.post(ApiEndpoints.AddStandard, payload);
                  const response = await fetchStandards();
                  setStandards(response.data as any[]);
                  setEditDialogVisible(false);
                  setIsAddMode(false);
                  toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Standard added successfully', life: 3000 });
                } catch (err: any) {
                  let msg = err?.response?.data?.message || err?.message || 'Failed to add standard';
                  console.error("Failed to add standard: " + msg);
                  toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
                } finally {
                  setLoading(false);
                }
              } else if (selectedStandard) {
                try {
                  setLoading(true);
                  await httpService.put(`${ApiEndpoints.UpdateStandard}/${selectedStandard.standardId}`, payload);
                  const response = await fetchStandards();
                  setStandards(response.data as any[]);
                  setEditDialogVisible(false);
                  setIsAddMode(false);
                  toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Standard updated successfully', life: 3000 });
                } catch (err: any) {
                  let msg = err?.response?.data?.message || err?.message || 'Failed to update standard';
                  console.error("Failed to update standard: " + msg);
                  toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
                } finally {
                  setLoading(false);
                }
              }
            }} loading={loading} disabled={loading} autoFocus />
          </div>
        }
      >
        <StandardEditForm editStandard={selectedStandard} setEditStandard={setSelectedStandard} performanceAreas={performanceAreas} providerTypesOptions={providerTypeOptions}/>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        header="Confirm Delete"
        visible={deleteDialogVisible}
        style={{ width: '350px' }}
        onHide={() => setDeleteDialogVisible(false)}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button label="Yes" autoFocus={false} onClick={() => {
              setStandards(standards.filter(s => s.standardId !== (standardToDelete && standardToDelete.standardId)));
              setDeleteDialogVisible(false);
            }} className="p-button-danger" />
            <Button label="No" autoFocus={true} onClick={() => setDeleteDialogVisible(false)} className="p-button-secondary" />
          </div>
        }
      >
        <div style={{ fontSize: 16, margin: '16px 0' }}>
          Are you sure you want to delete this record{standardToDelete && standardToDelete.title ? `: ${standardToDelete.title}` : ''}?
        </div>
      </Dialog>
    </div>
  );
};

interface StandardEditFormProps {
  editStandard: any;
  setEditStandard: (standard: any) => void;
  performanceAreas: any[];
  providerTypesOptions:{
 label: string;
 value: string;
}[];
}

const StandardEditForm: React.FC<StandardEditFormProps> = ({ editStandard, setEditStandard, performanceAreas,providerTypesOptions }) => {
  React.useEffect(() => {
    if (editStandard && editStandard.performanceAreaId && !editStandard.performanceAreaTitle) {
      const pa = performanceAreas.find(pa => pa.performanceAreaId === editStandard.performanceAreaId);
      if (pa) setEditStandard({ ...editStandard, performanceAreaTitle: pa.performanceAreaTitle });
    }
  }, [editStandard, performanceAreas, setEditStandard]);

  const filteredPerformanceAreas = performanceAreas.filter(pa =>
    (pa.isActive && (!editStandard?.providerType || pa.providerType === editStandard.providerType)) ||
    pa.performanceAreaId === editStandard?.performanceAreaId
  );

  return (
    <DataManagementForm
      fields={dataManagementFormConfig.standards}
      values={editStandard}
      onChange={setEditStandard}
      options={{
        providerType: providerTypesOptions,
        performanceAreaTitle: filteredPerformanceAreas.map(area => ({
          label: area.performanceAreaTitle,
          value: area.performanceAreaTitle,
          id: area.performanceAreaId,
        })),
      }}
    />
  );
};

export default StandardsComponent;